import { useQuery } from "@tanstack/react-query";
import fetchData from "@/lib/Fetchers/fetchData";
import { sendKibanaLog } from "./sendKibanaLog";
import {
  ReviewsResponse,
  TravellerType,
  SortOrder,
} from "@/lib/Types/ReviewsResponse/ReviewsResponse";
import { ALL_TRAVELLER_TYPES } from "@/lib/StaticData/reviewFilters";
import { AppConfig } from "@/lib/Types/AppConfig";

interface ReviewsParams {
  cid: string;
  m: number;
  p: number;
  page: string;
  sort: string;
  sortOrder: SortOrder;
  travellerTypes: TravellerType[];
  overview: boolean;
  portals?: string[];
}

interface UseReviewsParams {
  cid: string;
  page?: string;
  sort?: string;
  sortOrder?: SortOrder;
  travellerTypes?: TravellerType[];
  portals?: string[];
  enabled?: boolean;
  appConfig: AppConfig;
}

interface FetchConfig {
  envConfig: any;
  viewMode: any;
}

async function fetchReviews(
  params: Omit<UseReviewsParams, "enabled" | "appConfig">,
  config: FetchConfig,
  searchId: string,
): Promise<ReviewsResponse | null> {
  const { envConfig, viewMode } = config;
  const {
    cid,
    page = "1_6",
    sort = "DATE_RATING",
    sortOrder = "desc",
    travellerTypes = [],
    portals,
  } = params;

  // When no traveller types selected, send all types (API requires at least one)
  const effectiveTravellerTypes =
    travellerTypes.length > 0 ? travellerTypes : ALL_TRAVELLER_TYPES;

  const requestParams: ReviewsParams = {
    cid,
    m: viewMode.mandator,
    p: viewMode.profile,
    page,
    sort,
    sortOrder,
    travellerTypes: effectiveTravellerTypes,
    overview: true,
    ...(portals && portals.length > 0 && { portals }),
  };

  sendKibanaLog({
    envConfig,
    message: "FETCH REVIEWS START",
    component: "reviews",
    jsonMessage: requestParams,
    searchId: searchId,
    category: "debug",
  });

  const url = `${envConfig.api}/comvel-review-service/rest/hotel/review/list`;
  const response = await fetchData({
    url,
    method: "POST",
    params: requestParams,
    envConfig,
    service: "reviewsList",
    searchId: searchId,
  });

  const isError = response.apiError;

  sendKibanaLog({
    envConfig,
    message: `FETCH REVIEWS ${isError ? "FAIL" : "SUCCESS"}`,
    component: "reviews",
    jsonMessage: isError ? response.message : {},
    searchId: searchId,
    category: "debug",
  });

  return isError ? null : response;
}

const useReviews = ({
  cid,
  page = "1_6",
  sort = "DATE_RATING",
  sortOrder = "desc",
  travellerTypes = [],
  portals,
  enabled = true,
  appConfig,
}: UseReviewsParams) => {
  const { envConfig, viewMode, searchParams } = appConfig;

  // Serialize arrays for stable query key
  const travellerTypesKey = travellerTypes.join(",");
  const portalsKey = portals?.join(",") ?? "";
  const searchId = searchParams.searchId;

  const query = useQuery({
    queryKey: [
      "reviews",
      cid,
      page,
      sort,
      sortOrder,
      travellerTypesKey,
      portalsKey,
      viewMode?.mandator,
      viewMode?.profile,
    ],
    queryFn: () =>
      fetchReviews(
        { cid, page, sort, sortOrder, travellerTypes, portals },
        { envConfig, viewMode },
        searchId,
      ),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: enabled && !!envConfig && !!viewMode && !!cid,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
};

export default useReviews;
